import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { formatDateTimeLocal } from "@/shared/lib/date-time-performance";
import { buildPerformanceRequestWhatsAppUrl } from "@/shared/lib/whatsapp";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { WhatsAppLinkButton } from "@/shared/ui/WhatsAppLinkButton";
import { Badge, Button } from "@devhop/ui";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import {
  AlignLeftIcon,
  ArrowLeftIcon,
  CalendarClockIcon,
  ClockIcon,
  EditIcon,
  ExternalLinkIcon,
  MapPinIcon,
  PlusIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type {
  AssignmentDTO,
  CreateAssignmentDTO,
  UpdateAssignmentDTO,
} from "../../domain/contracts";
import {
  useBulkCreateAssignments,
  useBulkUpdateAssignmentShows,
  useAddEventShowToAllAssignments,
  useCreateAssignment,
  useDeleteAssignment,
  useEventDateChangesQuery,
  useEventDetailReportQuery,
  useEventQuery,
  useEventShowsQuery,
  useEventStaffQuery,
  usePartialShowParticipationQuery,
  useRehearsalSessionsQuery,
  useRehearsalSummaryQuery,
  useRequestQuery,
  useEventCalendarSyncQuery,
  useResyncEventCalendar,
  useUpdateAssignment,
} from "../api/queries";
import {
  eventDisplayCode,
  isEventDetailTabAvailable,
  isStaffPayoutEventReady,
  resolveEventDetailTab,
  sectionToTab,
  type EventDetailSectionId,
  type EventDetailTabId,
} from "../lib/event-detail-ui";
import { eventHasFeature } from "../../domain/services/event-features";
import { EventFeatureChips } from "../ui/EventFeatureChips";
import { GoogleCalendarSyncCard } from "../ui/GoogleCalendarSyncCard";
import { CommissionsSection } from "../ui/CommissionsSection";
import { EventOutsourcesSection } from "../ui/EventOutsourcesSection";
import { ProductionCostsSection } from "../ui/ProductionCostsSection";
import { EventDetailPageSkeleton } from "../ui/EventDetailPageSkeleton";
import { EventDetailPermissionNotice } from "../ui/EventDetailPermissionNotice";
import { EventDetailStepper } from "../ui/EventDetailStepper";
import { EventPostponeDialog } from "../ui/EventPostponeDialog";
import { EventPostponeHistory } from "../ui/EventPostponeHistory";
import { EventProgressCard } from "../ui/EventProgressCard";
import { EventRehearsalTrackingCard } from "../ui/EventRehearsalTrackingCard";
import { EventReportsTab } from "../ui/EventReportsTab";
import { EventShowsSchedule } from "../ui/EventShowsSchedule";
import { EventTeamPanelTabs } from "../ui/EventTeamPanelTabs";
import {
  EventTeamAssignDialog,
  type EventTeamAssignDialogMode,
} from "../ui/EventTeamAssignDialog";
import { EventStaffPayoutSection } from "../ui/EventStaffPayoutSection";
import { EventStaffAssignSection } from "../ui/EventStaffAssignSection";
import { PayoutsSection } from "../ui/PayoutsSection";
import { PricingSection } from "../ui/PricingSection";

const STATUS_LABELS: Record<string, string> = {
  draft: "ກຳລັງວາງແຜນ",
  confirmed: "ຍືນຍັນແລ້ວ",
  completed: "ສຳເລັດ",
  cancelled: "ຍົກເລີກ",
};
const STATUS_VARIANTS: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  draft: "secondary",
  confirmed: "default",
  completed: "outline",
  cancelled: "destructive",
};

function fmtDateTime(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return formatDateTimeLocal(new Date(d));
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.slice(0, 2) ?? "").toUpperCase();
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

function buildLocationSummary(event: {
  locationText: string | null;
  locationProvince: string | null;
}): string {
  const parts = [event.locationText, event.locationProvince].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

type TeamPanelState =
  | { open: false }
  | { open: true; mode: "create" }
  | { open: true; mode: "edit"; assignment: AssignmentDTO };

export function EventDetailPage() {
  const { id } = useParams({ from: "/app/performance/events/$id" });
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  const search = useSearch({ from: "/app/performance/events/$id" });
  const { data: event, isLoading } = useEventQuery(id);
  const calendarSync = useEventCalendarSyncQuery(id);
  const resyncCalendar = useResyncEventCalendar(id);
  const [postponeOpen, setPostponeOpen] = useState(false);
  const canEditEvent = useActionPermission(["performance-events:update"]);
  const canReadAssignments = useActionPermission([
    "performance-assignments:read",
  ]);
  const canCreateAssignment = useActionPermission([
    "performance-assignments:create",
  ]);
  const canUpdateAssignment = useActionPermission([
    "performance-assignments:update",
  ]);
  const canDeleteAssignment = useActionPermission([
    "performance-assignments:delete",
  ]);
  const canReadRequests = useActionPermission(["performance-requests:read"]);
  const canReadDocuments = useActionPermission(["performance-documents:read"]);
  const canReadPayouts = useActionPermission(["performance-payouts:read"]);
  const canReadEvent = useActionPermission(["performance-events:read"]);

  const eventFeatures = event?.eventFeatures ?? {
    performers: true,
    rehearsals: true,
    event_shows: true,
    documents: true,
    commissions: true,
    production_costs: true,
    outsources: false,
    staff: false,
  };
  const hasPerformers = eventHasFeature(eventFeatures, "performers");
  const hasStaff = eventHasFeature(eventFeatures, "staff");
  const staffPrimary = hasStaff && !hasPerformers;
  const hasCommissions = eventHasFeature(eventFeatures, "commissions");
  const hasProductionCosts = eventHasFeature(eventFeatures, "production_costs");
  const hasOutsources = eventHasFeature(eventFeatures, "outsources");
  const hasDocuments = eventHasFeature(eventFeatures, "documents");
  const hasRehearsals = eventHasFeature(eventFeatures, "rehearsals");
  const hasEventShows = eventHasFeature(eventFeatures, "event_shows");

  const activeTab = resolveEventDetailTab(search.section);
  const isOverviewTab = activeTab === "overview";
  const isTeamTab = activeTab === "team";
  const isStaffTab = activeTab === "staff";
  const isPricingTab = activeTab === "pricing";
  const isPayoutTab = activeTab === "payout";
  const isReportsTab = activeTab === "reports";

  const dateChangesQuery = useEventDateChangesQuery(id, !!event && isOverviewTab);
  const requestQuery = useRequestQuery(
    event?.requestId ?? "",
    isOverviewTab && canReadRequests && !!event?.requestId,
  );
  const showsQuery = useEventShowsQuery(
    id,
    !!event &&
      ((isOverviewTab && hasEventShows) ||
        isTeamTab ||
        (isReportsTab && (hasEventShows || hasPerformers))),
  );
  const staffQuery = useEventStaffQuery(id, {
    activeOnly: false,
    enabled: !!event && hasStaff && (isStaffTab || isPayoutTab),
  });
  const rehearsalSummaryQuery = useRehearsalSummaryQuery(
    id,
    !!event &&
      (isTeamTab || isReportsTab) &&
      hasRehearsals &&
      canReadAssignments,
  );
  const rehearsalSessionsQuery = useRehearsalSessionsQuery(
    id,
    !!event &&
      (isTeamTab || isReportsTab) &&
      hasRehearsals &&
      canReadAssignments,
  );

  const createAssignment = useCreateAssignment(id);
  const bulkCreateAssignments = useBulkCreateAssignments(id);
  const bulkUpdateAssignmentShows = useBulkUpdateAssignmentShows(id);
  const addShowToAllAssignments = useAddEventShowToAllAssignments(id);
  const partialShowParticipationQuery = usePartialShowParticipationQuery(
    id,
    isReportsTab && (showsQuery.data?.length ?? 0) >= 2,
  );
  const eventDetailReportQuery = useEventDetailReportQuery(
    id,
    isReportsTab && canReadEvent,
  );
  const deleteAssignment = useDeleteAssignment(id);
  const [teamPanel, setTeamPanel] = useState<TeamPanelState>({ open: false });
  const updateAssignment = useUpdateAssignment(
    id,
    teamPanel.open && teamPanel.mode === "edit" ? teamPanel.assignment.id : "",
  );

  const assignments = event?.assignments ?? [];
  const teamCount = assignments.length;
  const staffCount = (staffQuery.data ?? []).filter((row) => row.isActive).length;
  const derivedShowCount = Math.max(1, showsQuery.data?.length ?? 1);

  const staffPayoutReady = useMemo(
    () => (event ? isStaffPayoutEventReady(event) : false),
    [event],
  );

  const workflow = useMemo(() => {
    const milestones = event?.progress?.milestones;
    return {
      team: !!milestones?.team,
      staff: hasStaff && staffCount > 0,
      pricing: !!milestones?.pricing,
      payout: !!milestones?.payout,
    };
  }, [event?.progress?.milestones, hasStaff, staffCount]);

  useEffect(() => {
    if (!event) return;
    if (isEventDetailTabAvailable(activeTab, eventFeatures)) return;
    nav({
      search: (prev) => ({ ...prev, section: "overview" as EventDetailTabId }),
      replace: true,
    });
  }, [activeTab, event, eventFeatures, nav]);

  const handleTabClick = (tab: EventDetailTabId) => {
    nav({
      search: (prev) => ({ ...prev, section: tab }),
      replace: true,
    });
  };

  const handleSectionClick = (section: EventDetailSectionId) => {
    handleTabClick(sectionToTab(section));
  };

  const closeTeamPanel = () => setTeamPanel({ open: false });

  const openTeamPanel = (
    mode: EventTeamAssignDialogMode,
    assignment?: AssignmentDTO,
  ) => {
    handleTabClick("team");
    if (mode === "edit" && assignment) {
      setTeamPanel({ open: true, mode: "edit", assignment });
      return;
    }
    setTeamPanel({ open: true, mode: "create" });
  };

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ງານສະແດງ", to: "/app/performance/events" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <EventDetailPageSkeleton />
        </Main>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ງານສະແດງ", to: "/app/performance/events" },
              { label: "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <p className="text-destructive">ບໍ່ພົບງານສະແດງ</p>
        </Main>
      </>
    );
  }

  const locationSummary = buildLocationSummary(event);
  const customerRequest = requestQuery.data ?? null;
  const dateChanges = dateChangesQuery.data?.items ?? [];
  const canPostpone =
    canEditEvent &&
    (event.status === "draft" || event.status === "confirmed");

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: event.title },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="font-bold text-2xl tracking-tight sm:text-3xl">
                {event.title}
              </h1>
              <Badge
                variant={STATUS_VARIANTS[event.status] ?? "secondary"}
                className="mt-0.5"
              >
                {STATUS_LABELS[event.status] ?? event.status}
              </Badge>
              {(event.wasPostponed ||
                event.dateTbd ||
                (event.postponeCount ?? 0) > 0) && (
                <Badge
                  className="mt-0.5 border-transparent bg-amber-500 text-white hover:bg-amber-500"
                >
                  {event.dateTbd ? "ຍັງບໍ່ກຳນົດ" : "ເລື່ອນ"}
                </Badge>
              )}
              <Badge variant="outline" className="mt-0.5 gap-1.5">
                {event.categoryName ?? "—"}
              </Badge>
              <EventFeatureChips features={eventFeatures} className="mt-0.5" />
              {event.isProvincial && (
                <Badge variant="outline" className="mt-0.5">
                  ຕ່າງແຂວງ
                  {event.perDiemDays != null && event.perDiemDays > 1
                    ? ` · ${event.perDiemDays} ວັນ`
                    : null}
                </Badge>
              )}
            </div>
            <p className="mt-1.5 text-muted-foreground text-sm leading-relaxed">
              {eventDisplayCode(event.id)} ·{" "}
              {event.dateTbd
                ? "ຍັງບໍ່ກຳນົດວັນ"
                : fmtDateTime(event.eventDate)}
              {!event.dateTbd && event.endDate
                ? ` – ${fmtDateTime(event.endDate)}`
                : ""}{" "}
              · {locationSummary}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/performance/events" })}
            >
              <ArrowLeftIcon className="size-4" /> ກັບຄືນ
            </Button>
            {event.requestId && canReadRequests && (
              <Button
                variant="outline"
                onClick={() =>
                  nav({
                    to: "/app/performance/requests/$id",
                    params: { id: event.requestId ?? "" },
                  })
                }
              >
                <ExternalLinkIcon className="size-4" /> ຄຳຂໍ
              </Button>
            )}
            {canPostpone && (
              <Button
                variant="outline"
                onClick={() => setPostponeOpen(true)}
              >
                <CalendarClockIcon className="size-4" /> ເລື່ອນງານ
              </Button>
            )}
            {canEditEvent && (
              <Button
                onClick={() =>
                  nav({
                    to: "/app/performance/events/$id/edit",
                    params: { id },
                  })
                }
              >
                <EditIcon className="size-4" /> ແກ້ໄຂງານ
              </Button>
            )}
          </div>
        </div>

        <GoogleCalendarSyncCard
          status={calendarSync.data}
          canSync={canEditEvent}
          isSyncing={resyncCalendar.isPending}
          onSync={() => resyncCalendar.mutate()}
        />

        <EventPostponeDialog
          event={event}
          open={postponeOpen}
          onOpenChange={setPostponeOpen}
        />

        <EventDetailStepper
          activeTab={activeTab}
          onTabClick={handleTabClick}
          workflow={workflow}
          teamCount={teamCount}
          staffCount={staffCount}
          eventFeatures={eventFeatures}
        />

        {isOverviewTab ? (
        <section
          role="tabpanel"
          id="event-detail-panel-overview"
          aria-labelledby="event-detail-tab-overview"
          className="mb-6"
        >
          <div className="mb-4">
            <EventPostponeHistory
              items={dateChanges}
              isLoading={dateChangesQuery.isLoading}
            />
          </div>
          <h2 id="event-overview-heading" className="sr-only">
            ພາບລວມງານ
          </h2>
          <div className="grid grid-cols-1 divide-y rounded-xl border bg-card md:grid-cols-3 md:divide-x md:divide-y-0">
            <div className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <ClockIcon className="size-4 text-primary" />
                <span className="font-semibold text-xs uppercase tracking-widest">
                  ຕາລາງເວລາ
                </span>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ເວລາເລີ່ມ
                  </p>
                  <p className="mt-0.5 font-bold text-lg">
                    {event.dateTbd
                      ? "ຍັງບໍ່ກຳນົດ"
                      : fmtDateTime(event.eventDate)}
                  </p>
                </div>
                {event.endDate && (
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">
                      ເວລາສິ້ນສຸດ
                    </p>
                    <p className="mt-0.5 font-bold text-lg">
                      {fmtDateTime(event.endDate)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <MapPinIcon className="size-4 text-primary" />
                <span className="font-semibold text-xs uppercase tracking-widest">
                  ສະຖານທີ່
                </span>
              </div>
              {event.locationText ? (
                <p className="font-bold text-xl">{event.locationText}</p>
              ) : (
                <p className="text-muted-foreground text-sm">—</p>
              )}
              {event.locationProvince && (
                <p className="mt-2 text-muted-foreground text-sm">
                  ແຂວງ: {event.locationProvince}
                </p>
              )}
            </div>

            <div className="p-5">
              <div className="mb-4 flex items-center gap-2">
                <AlignLeftIcon className="size-4 text-primary" />
                <span className="font-semibold text-xs uppercase tracking-widest">
                  ໝາຍເຫດການຜະລິດ
                </span>
              </div>
              {event.notes ? (
                <p className="text-sm leading-relaxed">{event.notes}</p>
              ) : (
                <p className="text-muted-foreground text-sm italic">
                  ຍັງບໍ່ມີໝາຍເຫດ
                </p>
              )}
            </div>
          </div>
          {hasEventShows ? (
            <EventShowsSchedule eventId={event.id} canManage={canEditEvent} />
          ) : null}
          <EventProgressCard
            progress={event.progress}
            eventFeatures={eventFeatures}
            onSectionClick={handleSectionClick}
          />
          {customerRequest && (
            <div className="mt-4 rounded-xl border bg-card p-5">
              <div className="mb-4 flex items-center gap-2">
                <UserIcon className="size-4 text-primary" />
                <span className="font-semibold text-xs uppercase tracking-widest">
                  ຂໍ້ມູນລູກຄ້າ
                </span>
              </div>
              <div className="grid gap-4 text-sm md:grid-cols-5">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ລູກຄ້າ
                  </p>
                  <p className="mt-1 font-semibold">
                    {customerRequest.customerName || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ຜູ້ປະສານງານ
                  </p>
                  <p className="mt-1 font-semibold">
                    {customerRequest.contactName}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ເບີໂທ
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <p>{customerRequest.contactPhone ?? "—"}</p>
                    {customerRequest.contactPhone ? (
                      <WhatsAppLinkButton
                        phone={customerRequest.contactPhone}
                        href={buildPerformanceRequestWhatsAppUrl({
                          phone: customerRequest.contactPhone,
                          contactName: customerRequest.contactName,
                          requestTitle: customerRequest.title,
                        })}
                        label="WhatsApp"
                      />
                    ) : null}
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ອີເມວ
                  </p>
                  <p className="mt-1 break-all">
                    {customerRequest.contactEmail ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ທີ່ຢູ່
                  </p>
                  <p className="mt-1 whitespace-pre-wrap">
                    {customerRequest.contactAddress ?? "—"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>
        ) : null}

        {isTeamTab && hasPerformers ? (
          <>
            {hasRehearsals && canReadAssignments ? (
              <section
                className="mb-6"
                aria-label="ປະຕິທິນຊ້ອມ"
              >
                <EventRehearsalTrackingCard
                  hasTeam={assignments.length > 0}
                  summary={rehearsalSummaryQuery.data}
                  sessions={rehearsalSessionsQuery.data}
                  isLoading={
                    rehearsalSummaryQuery.isLoading ||
                    rehearsalSessionsQuery.isLoading
                  }
                  onOpenSchedule={() =>
                    nav({
                      to: "/app/performance/events/$id/rehearsals",
                      params: { id },
                    })
                  }
                />
              </section>
            ) : null}

            <section
              role="tabpanel"
              id="event-detail-panel-team"
              aria-labelledby="event-detail-tab-team"
            >
              <div className="rounded-xl border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
                <div>
                  <div className="flex items-center gap-2">
                    <UserIcon className="size-4 text-primary" />
                    <h2
                      id="event-team-heading"
                      className="font-semibold text-sm uppercase tracking-widest"
                    >
                      ນັກສະແດງທີ່ຜູກງານ
                    </h2>
                  </div>
                  {assignments.length > 0 ? (
                    <p className="mt-1 pl-6 text-muted-foreground text-xs">
                      {new Set(assignments.map((row) => row.performerId)).size}{" "}
                      ຄົນ · {assignments.length} ບັນທັດຄ່າຕົວ
                    </p>
                  ) : null}
                </div>
                {canReadAssignments && canCreateAssignment && (
                  <Button size="sm" onClick={() => openTeamPanel("create")}>
                    <PlusIcon className="size-3.5" />
                    ເພີ່ມນັກສະແດງ
                  </Button>
                )}
              </div>

              {!canReadAssignments ? (
                <EventDetailPermissionNotice
                  title="ບໍ່ມີສິດເບິ່ງການຈັດທີມ"
                  description="ຕ້ອງມີສິດ performance-assignments:read ເພື່ອເບິ່ງ ແລະ ຈັດການນັກສະແດງໃນງານ"
                />
              ) : (
                <>
                  {!assignments.length ? (
                    <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
                      <p className="text-muted-foreground text-sm">
                        {canCreateAssignment
                          ? "ຍັງບໍ່ມີນັກສະແດງ — ເລີ່ມຈາກຂັ້ນຕອນ ຈັດທີມ"
                          : "ຍັງບໍ່ມີນັກສະແດງ — ຕ້ອງມີສິດ performance-assignments:create ເພື່ອເພີ່ມທີມ"}
                      </p>
                      {canCreateAssignment && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openTeamPanel("create")}
                        >
                          <PlusIcon className="size-3.5" />
                          ເພີ່ມນັກສະແດງ
                        </Button>
                      )}
                    </div>
                  ) : (
                    <EventTeamPanelTabs
                      eventId={id}
                      assignments={assignments}
                      eventShows={showsQuery.data ?? []}
                      canUpdate={canUpdateAssignment}
                      canDelete={canDeleteAssignment}
                      deletePending={deleteAssignment.isPending}
                      matrixSubmitting={bulkUpdateAssignmentShows.isPending}
                      onBulkUpdate={async (updates) => {
                        await bulkUpdateAssignmentShows.mutateAsync({ updates });
                      }}
                      onAddShowToAll={
                        canUpdateAssignment
                          ? async (eventShowId) => {
                              await addShowToAllAssignments.mutateAsync({
                                eventShowId,
                              });
                            }
                          : undefined
                      }
                      getInitials={getInitials}
                      onViewPerformer={(performerId) =>
                        nav({
                          to: "/app/performance/performers/$id",
                          params: { id: performerId },
                        })
                      }
                      onEdit={(assignment) => openTeamPanel("edit", assignment)}
                      onDelete={(assignmentId) =>
                        deleteAssignment.run(assignmentId)
                      }
                    />
                  )}
                </>
              )}
              </div>
            </section>
          </>
        ) : null}

        {isStaffTab && hasStaff ? (
          <div
            role="tabpanel"
            id="event-detail-panel-staff"
            aria-labelledby="event-detail-tab-staff"
            className="mb-6"
          >
            {canReadPayouts ? (
              <EventStaffAssignSection eventId={id} />
            ) : (
              <EventDetailPermissionNotice
                title="ບໍ່ມີສິດເບິ່ງ Staff"
                description="ຕ້ອງມີສິດ performance-payouts:read ເພື່ອເບິ່ງທີມ Staff"
              />
            )}
          </div>
        ) : null}

        {isPricingTab && hasDocuments ? (
          <div
            role="tabpanel"
            id="event-detail-panel-pricing"
            aria-labelledby="event-detail-tab-pricing"
          >
            {canReadDocuments ? (
              <PricingSection
                eventId={id}
                assignments={assignments}
                eventShows={showsQuery.data}
                hasPerformers={hasPerformers}
              />
            ) : (
              <EventDetailPermissionNotice
                title="ບໍ່ມີສິດເບິ່ງລາຄາ"
                description="ຕ້ອງມີສິດ performance-documents:read ເພື່ອເບິ່ງໃບສະເໜີລາຄາ ແລະ ໃບເກັບເງິນ"
              />
            )}
          </div>
        ) : null}

        {isPayoutTab &&
        (hasPerformers ||
          hasStaff ||
          hasCommissions ||
          hasProductionCosts ||
          hasOutsources) ? (
        <div
          role="tabpanel"
          id="event-detail-panel-payout"
          aria-labelledby="event-detail-tab-payout"
          className="mt-6 grid items-stretch gap-4 lg:grid-cols-2"
        >
          {hasStaff && staffPrimary ? (
            <div className="min-w-0">
              {canReadPayouts ? (
                <EventStaffPayoutSection
                  eventId={id}
                  payoutReady={staffPayoutReady}
                  onGoToStaff={() => handleTabClick("staff")}
                />
              ) : (
                <EventDetailPermissionNotice
                  className="h-full"
                  title="ບໍ່ມີສິດເບິ່ງເບີກຈ່າຍ"
                  description="ຕ້ອງມີສິດ performance-payouts:read ເພື່ອເບິ່ງໃບເບີກຈ່າຍ Staff"
                />
              )}
            </div>
          ) : null}

          {hasPerformers ? (
            <div className="min-w-0">
              {canReadPayouts ? (
                <PayoutsSection
                  eventId={id}
                  assignments={assignments}
                  onGoToTeam={() => handleTabClick("team")}
                />
              ) : (
                <EventDetailPermissionNotice
                  className="h-full"
                  title="ບໍ່ມີສິດເບິ່ງເບີກຈ່າຍ"
                  description="ຕ້ອງມີສິດ performance-payouts:read ເພື່ອເບິ່ງໃບເບີກຈ່າຍຄ່າສະແດງ"
                />
              )}
            </div>
          ) : null}

          {hasStaff && !staffPrimary ? (
            <div className="min-w-0">
              {canReadPayouts ? (
                <EventStaffPayoutSection
                  eventId={id}
                  payoutReady={staffPayoutReady}
                  onGoToStaff={() => handleTabClick("staff")}
                />
              ) : (
                <EventDetailPermissionNotice
                  className="h-full"
                  title="ບໍ່ມີສິດເບິ່ງເບີກຈ່າຍ"
                  description="ຕ້ອງມີສິດ performance-payouts:read ເພື່ອເບິ່ງໃບເບີກຈ່າຍ Staff"
                />
              )}
            </div>
          ) : null}

          {hasCommissions ? (
          <div className="min-w-0">
            {canReadPayouts ? (
              <CommissionsSection eventId={id} />
            ) : (
              <EventDetailPermissionNotice
                className="h-full"
                title="ບໍ່ມີສິດເບິ່ງຄ່ານາຍໜ້າ"
                description="ຕ້ອງມີສິດ performance-payouts:read ເພື່ອເບິ່ງຄ່ານາຍໜ້າ"
              />
            )}
          </div>
          ) : null}

          {hasProductionCosts ? (
          <div className="min-w-0">
            {canReadEvent ? (
              <ProductionCostsSection eventId={id} />
            ) : (
              <EventDetailPermissionNotice
                className="h-full"
                title="ບໍ່ມີສິດເບິ່ງຕົ້ນທຸນ"
                description="ຕ້ອງມີສິດ performance-events:read ເພື່ອເບິ່ງຕົ້ນທຸນ prop / ວັດສະດຸ"
              />
            )}
          </div>
          ) : null}

          {hasOutsources ? (
          <div className="min-w-0">
            {canReadEvent ? (
              <EventOutsourcesSection eventId={id} />
            ) : (
              <EventDetailPermissionNotice
                className="h-full"
                title="ບໍ່ມີສິດເບິ່ງ Outsource"
                description="ຕ້ອງມີສິດ performance-events:read ເພື່ອເບິ່ງ Outsource (KOL/Media)"
              />
            )}
          </div>
          ) : null}
        </div>
        ) : null}

        {isReportsTab &&
        isEventDetailTabAvailable("reports", eventFeatures) ? (
          <div
            role="tabpanel"
            id="event-detail-panel-reports"
            aria-labelledby="event-detail-tab-reports"
            className="mb-6"
          >
            {!canReadEvent ? (
              <EventDetailPermissionNotice
                title="ບໍ່ມີສິດເບິ່ງລາຍງານ"
                description="ຕ້ອງມີສິດ performance-events:read ເພື່ອເບິ່ງລາຍງານງານ"
              />
            ) : (
              <EventReportsTab
                eventId={id}
                event={event}
                eventFeatures={eventFeatures}
                assignments={assignments}
                eventShows={showsQuery.data ?? []}
                partialShowRows={partialShowParticipationQuery.data ?? []}
                rehearsalSummary={rehearsalSummaryQuery.data}
                rehearsalSessions={rehearsalSessionsQuery.data}
                isLoadingRehearsal={
                  rehearsalSummaryQuery.isLoading ||
                  rehearsalSessionsQuery.isLoading
                }
                report={eventDetailReportQuery.data}
                isLoadingReport={eventDetailReportQuery.isLoading}
                canReadAssignments={canReadAssignments}
                canReadDocuments={canReadDocuments}
                canReadPayouts={canReadPayouts}
                canUpdateEvent={canEditEvent}
                onTabClick={handleTabClick}
                onSectionClick={handleSectionClick}
                onOpenRehearsals={() =>
                  nav({
                    to: "/app/performance/events/$id/rehearsals",
                    params: { id },
                  })
                }
              />
            )}
          </div>
        ) : null}

        {hasPerformers &&
          (canCreateAssignment || canUpdateAssignment) && (
            <EventTeamAssignDialog
              open={teamPanel.open}
              mode={teamPanel.open ? teamPanel.mode : "create"}
              eventKind={event.categorySlug ?? event.eventKind ?? "stage_show"}
              derivedShowCount={derivedShowCount}
              eventShows={showsQuery.data}
              existingAssignments={assignments}
              editingAssignment={
                teamPanel.open && teamPanel.mode === "edit"
                  ? teamPanel.assignment
                  : null
              }
              createSubmitting={
                createAssignment.isPending || bulkCreateAssignments.isPending
              }
              updateSubmitting={updateAssignment.isPending}
              onOpenChange={(open) => {
                if (!open) closeTeamPanel();
              }}
              onCreate={async (vals: CreateAssignmentDTO) => {
                try {
                  await createAssignment.mutateAsync(vals);
                  closeTeamPanel();
                } catch { }
              }}
              onUpdate={async (vals) => {
                try {
                  const input = vals as UpdateAssignmentDTO;
                  await updateAssignment.mutateAsync({
                    roleFeeKind: input.roleFeeKind,
                    showCount: input.showCount,
                    rehearsalDays: input.rehearsalDays,
                    lineKey: input.lineKey,
                    notes: input.notes,
                  });
                  closeTeamPanel();
                } catch { }
              }}
              onApplyTemplates={async (items) => {
                try {
                  await bulkCreateAssignments.mutateAsync(items);
                  closeTeamPanel();
                } catch { }
              }}
            />
          )}
      </Main>
    </>
  );
}
